// 组队邀请 Hook - useTeamInvite.ts
// 管理组队邀请的状态和业务逻辑

import { ref, reactive, computed, watch, inject } from 'vue';
import { getJSON } from '@/api/request';
import { getActivityApi } from '@/utils';
import { defaultTeamInviteConfig, TeamAward, TeamMember } from '@/config/teamInviteConfig';
import type { Ref } from 'vue';

// 类型定义
export interface TeamInviteConfig {
  platformId?: string;
  activityName?: string;
  gameId?: number;
}

export interface TeamInviteState {
  // 队伍信息
  curTeamSize: number; // 当前队伍人数
  teamSize: number; // 队伍最大人数
  isCaptain: boolean; // 是否是队长
  canExit: boolean; // 是否可以退出队伍

  // 成员信息
  memberInfo: TeamMember[];

  // 奖励信息
  awardInfo: TeamAward[];
  leaderAwardList: TeamAward[];
  showLeaderAward: boolean;
}

/**
 * 组队邀请 Hook
 */
export function useTeamInvite(config: TeamInviteConfig = {}) {
  const { platformId = 'global', activityName = 'row_reservation_01', gameId = 386 } = config;

  // 注入全局分享弹窗方法
  const showSharePopupFn = inject<(mode: string) => void>('showSharePopup', () => {});

  // ========== 状态管理 ==========

  const state = reactive<TeamInviteState>({
    curTeamSize: 0,
    teamSize: defaultTeamInviteConfig.teamSize,
    isCaptain: false,
    canExit: true,
    memberInfo: [...defaultTeamInviteConfig.memberInfo],
    awardInfo: [...defaultTeamInviteConfig.awardInfo],
    leaderAwardList: [...defaultTeamInviteConfig.leaderAwardList],
    showLeaderAward: defaultTeamInviteConfig.showLeaderAward,
  });

  // 弹窗状态
  const showEventRule = ref(false); // 活动规则弹窗
  const showBreakTeam = ref(false); // 解散队伍弹窗
  const showExitTeam = ref(false); // 退出队伍弹窗
  const showAwardDetail = ref(false); // 奖励详情弹窗
  const showBreakTeamTip = ref(false); // 解散队伍提示弹窗

  // ========== 计算属性 ==========

  // 队伍是否已满
  const isTeamFull = computed(() => state.curTeamSize >= state.teamSize);

  // 是否有队伍
  const hasTeam = computed(() => state.curTeamSize > 0);

  // ========== 核心功能函数 ==========

  /**
   * 初始化队伍信息
   */
  const initTeamInfo = async (data?: any) => {
    if (data) {
      // 如果有 teamCode，说明玩家有队伍，需要调用 getTeamMembers 获取完整队伍信息
      if (data.teamCode) {
        try {
          const url = getActivityApi(platformId, activityName, 'getTeamMembers');
          const res = await getJSON(url, {
            params: JSON.stringify({})
          });

          if (res.code === 1 && res.result === 1 && res.data?.members) {
            const members = res.data.members;
            state.curTeamSize = members.length;
            state.memberInfo = members.map((member: any, index: number) => ({
              name: member.nickname || member.roleName || member.role_name || '',
              leader: index === 0 || member.role === 'leader',
            }));
          } else {
            // API 调用失败，降级处理：至少设置 curTeamSize = 1
            state.curTeamSize = 1;
          }
        } catch (error) {
          console.error('❌ [useTeamInvite] getTeamMembers 调用异常:', error);
          // 异常情况降级处理
          state.curTeamSize = 1;
        }
      }

      // 更新队伍人数（如果 data 中直接有 teamList）
      if (data.teamList && Array.isArray(data.teamList)) {
        state.curTeamSize = data.teamList.length;
        state.memberInfo = data.teamList.map((item: any, index: number) => ({
          name: item.roleName || item.role_name || item.loginAccount || item.login_account || '',
          leader: index === 0,
        }));
      }

      // 更新队伍奖励状态
      if (data.teamAward && Array.isArray(data.teamAward)) {
        state.awardInfo = state.awardInfo.map((confItem, index) => {
          const apiItem = data.teamAward[index];
          if (apiItem) {
            return {
              ...confItem,
              num: apiItem.teamSize || confItem.num,
              state: apiItem.status || 0,
              key: apiItem.key,
            };
          }
          return confItem;
        });
      }

      // 更新队长奖励状态
      if (data.captainAward && Array.isArray(data.captainAward)) {
        state.leaderAwardList = state.leaderAwardList.map((confItem, index) => {
          const apiItem = data.captainAward[index];
          if (apiItem) {
            return {
              ...confItem,
              num: apiItem.teamSize || confItem.num,
              state: apiItem.status || 0,
              key: apiItem.key,
            };
          }
          return confItem;
        });
      }

      // 更新队长状态
      if (typeof data.isCaptain !== 'undefined') {
        state.isCaptain = Boolean(data.isCaptain);
      }

      // 更新是否可退出状态
      if (typeof data.canExit !== 'undefined') {
        state.canExit = Boolean(data.canExit);
      }
    }
  };

  /**
   * 刷新队伍信息
   */
  const refreshTeamInfo = async () => {
    console.log('🔄 刷新队伍信息');
    // TODO: 调用实际的 API 来刷新队伍信息
  };

  /**
   * 创建队伍
   */
  const createTeam = async (): Promise<{ success: boolean; teamCode?: string; code?: number; message?: string }> => {
    try {
      // 使用统一的 getActivityApi 构建 URL
      const url = getActivityApi(platformId, activityName, 'createTeam');
      const res = await getJSON(url, {
        params: JSON.stringify({})
      });

      if (res.code === 1 && res.result === 1) {
        const teamCode = res.data?.teamCode;
        state.curTeamSize = 1;
        state.isCaptain = true;
        return { success: true, teamCode };
      } else {
        console.error('❌ 创建队伍失败:', res.msg, 'code:', res.code);
        return { success: false, code: res.code, message: res.msg || '創建失敗' };
      }
    } catch (error) {
      console.error('❌ 创建队伍接口调用失败:', error);
      const errorMessage = error instanceof Error ? error.message : 'System Error';
      return { success: false, message: errorMessage };
    }
  };

  /**
   * 解散队伍
   */
  const breakTeam = async (): Promise<boolean> => {
    // 检查是否可以解散（队伍人数必须为1）
    if (state.curTeamSize > 1) {
      return false;
    }

    // TODO: 调用真实 API
    return false;
  };

  /**
   * 退出队伍
   */
  const exitTeam = async (): Promise<boolean> => {
    if (!state.canExit) {
      return false;
    }

    // TODO: 调用真实 API
    return false;
  };

  /**
   * 邀请好友
   *
   * @param initShareFn - App.vue 提供的初始化分享函数
   * @param updateShareLinkFn - App.vue 提供的更新分享链接函数
   */
  const inviteFriend = async (initShareFn?: Function, updateShareLinkFn?: Function): Promise<void> => {
    if (isTeamFull.value) {
      return;
    }

    // TODO: 调用真实 API 获取邀请链接
    // const res = await getJSON('/api/team/invite');
    // const inviteLink = res?.data?.inviteLink;
    // if (inviteLink) {
    //   const { getShortLink } = await import('@/hooks/shareHook');
    //   const finalLink = await getShortLink('invite', inviteLink) || inviteLink;
    //   if (initShareFn) {
    //     initShareFn(finalLink);
    //   } else if (updateShareLinkFn) {
    //     updateShareLinkFn(finalLink);
    //   }
    // }
  };

  /**
   * 加入其他队伍
   */
  const joinOtherTeam = async (inviteCode?: string): Promise<{ success: boolean; code?: number; message?: string }> => {
    if (!inviteCode) {
      return { success: false, message: '邀請碼不能為空' };
    }

    try {
      // 使用统一的 getActivityApi 构建 URL
      const url = getActivityApi(platformId, activityName, 'joinTeam');
      const res = await getJSON(url, {
        params: JSON.stringify({
          teamCode: inviteCode
        })
      });

      // 检查响应是否存在
      if (!res) {
        console.error('❌ 加入队伍失败: 接口无响应');
        return { success: false, message: '接口無響應' };
      }

      if (res.code === 1 && res.result === 1) {
        return { success: true };
      } else {
        console.error('❌ 加入队伍失败:', res.msg, 'code:', res.code, 'result:', res.result);
        // 返回错误码，让调用方使用 getLangTxt(code, getLangTxt('common')) 显示翻译
        return { success: false, code: res.code, message: res.msg || '加入失敗' };
      }
    } catch (error) {
      console.error('❌ 加入队伍接口调用失败:', error);
      // 返回更详细的错误信息
      const errorMessage = error instanceof Error ? error.message : 'System Error';
      return { success: false, message: errorMessage };
    }
  };

  /**
   * 领取奖励
   */
  const claimAward = async (
    awardType: 'team' | 'captain',
    awardKey?: string,
    groupIndex?: number
  ): Promise<{ success: boolean; code?: number; message?: string }> => {
    // 检查 awardKey 是否存在
    if (!awardKey) {
      console.error('❌ 领取奖励失败: awardKey 不存在');
      return { success: false, message: '獎勵 key 不存在' };
    }

    try {
      // 调用真实接口（与任务领奖相同）
      const url = getActivityApi(platformId, activityName, 'claimTaskCenterReward');
      const res = await getJSON(url, {
        params: JSON.stringify({
          taskKey: parseInt(awardKey), // 转为 int 类型
        }),
      });

      if (res.result === 1) {
        // 手动更新奖励状态为已领取
        if (awardType === 'team' && typeof groupIndex === 'number') {
          state.awardInfo[groupIndex].state = 2; // 已领取
        } else if (awardType === 'captain') {
          // 找到对应的队长奖励并更新状态
          const award = state.leaderAwardList.find(a => a.key === awardKey);
          if (award) {
            award.state = 2; // 已领取
          }
        }

        return { success: true, message: res.msg || '領取成功' };
      } else {
        console.error('❌ 领取奖励失败:', res.msg, 'code:', res.code);
        return { success: false, code: res.code, message: res.msg || '領取失敗' };
      }
    } catch (error) {
      console.error('❌ 领取奖励接口调用失败:', error);
      const errorMessage = error instanceof Error ? error.message : 'System Error';
      return { success: false, message: errorMessage };
    }
  };

  /**
   * 查看队伍详情
   */
  const viewTeamDetails = () => {
    // 这里可以触发显示队伍详情弹窗
  };

  /**
   * 获取队伍成员列表
   */
  const getTeamMembers = async (): Promise<{ success: boolean; members?: any[]; code?: number; message?: string }> => {

    try {
      // 使用统一的 getActivityApi 构建 URL
      const url = getActivityApi(platformId, activityName, 'getTeamMembers');
      const res = await getJSON(url, {
        params: JSON.stringify({})
      });

      if (res.code === 1 && res.result === 1) {
        const members = res.data?.members || [];
        // 将 role 映射为繁体中文
        const mappedMembers = members.map((member: any) => ({
          role: member.role === 'leader' ? '隊長' : '隊員',
          nickname: member.nickname,
          joinTime: member.joinTime,
        }));
        return { success: true, members: mappedMembers };
      } else {
        console.error('❌ 获取队伍成员列表失败:', res.msg, 'code:', res.code);
        return { success: false, code: res.code, message: res.msg || '獲取隊伍列表失敗' };
      }
    } catch (error) {
      console.error('❌ 获取队伍成员列表接口调用失败:', error);
      const errorMessage = error instanceof Error ? error.message : 'System Error';
      return { success: false, message: errorMessage };
    }
  };

  /**
   * 查看奖励详情
   */
  const currentAwardDetail = ref('');
  const viewAwardDetails = (award: TeamAward) => {
    if (award && award.awardDetail) {
      currentAwardDetail.value = award.awardDetail;
      showAwardDetail.value = true;
    }
  };

  /**
   * 触发邀请流程（供外部调用）
   * 用于任务侧等其他地方触发邀请功能
   */
  const triggerInvite = async (
    checkLoginFn?: () => Promise<boolean>,
    showTipsFn?: (msg: string) => void,
    initShareFn?: Function,
    updateShareLinkFn?: Function
  ): Promise<void> => {
    // 检查登录
    if (checkLoginFn) {
      const isLogin = await checkLoginFn();
      if (!isLogin) return;
    }

    // 检查队伍是否已满
    if (isTeamFull.value) {
      showTipsFn?.('队伍已满');
      return;
    }

    // 调用邀请好友逻辑
    await inviteFriend(initShareFn, updateShareLinkFn);

    // 显示分享弹窗 (组队邀请模式)
    showSharePopupFn?.('team_invite');
  };

  /**
   * 触发创建队伍（供外部调用）
   */
  const triggerCreateTeam = async (
    checkLoginFn?: () => Promise<boolean>,
    showTipsFn?: (msg: string) => void,
    myCodeRef?: Ref<string>,
    getLangTxtFn?: (code: number, defaultMsg: string) => string
  ): Promise<boolean> => {
    // 检查登录
    if (checkLoginFn) {
      const isLogin = await checkLoginFn();
      if (!isLogin) return false;
    }

    const result = await createTeam();
    if (result.success) {
      showTipsFn?.('隊伍創建成功');
      // 更新 myCode
      if (myCodeRef && result.teamCode) {
        myCodeRef.value = result.teamCode;
      }
    } else {
      // 失败时也显示错误提示
      let errorMsg = result.message || '創建失敗';
      if (result.code && getLangTxtFn) {
        // getLangTxt 第二个参数是默认值，传入 '請稍後再試'
        errorMsg = getLangTxtFn(result.code, '請稍後再試');
      }
      showTipsFn?.(errorMsg);
    }
    return result.success;
  };

  /**
   * 触发解散队伍确认弹窗（供外部调用）
   */
  const triggerBreakTeam = (showTipsFn?: (msg: string) => void): void => {
    if (state.memberInfo.length > 1) {
      showBreakTeamTip.value = true;
      return;
    }
    showBreakTeam.value = true;
  };

  /**
   * 确认解散队伍
   */
  const confirmBreakTeam = async (showTipsFn?: (msg: string) => void): Promise<void> => {
    const success = await breakTeam();
    if (success) {
      showTipsFn?.('队伍已解散');
      showBreakTeam.value = false;
    }
  };

  /**
   * 触发退出队伍确认弹窗（供外部调用）
   */
  const triggerExitTeam = (showTipsFn?: (msg: string) => void): void => {
    if (!state.canExit) {
      showTipsFn?.('当前无法退出队伍');
      return;
    }
    showExitTeam.value = true;
  };

  /**
   * 确认退出队伍
   */
  const confirmExitTeam = async (showTipsFn?: (msg: string) => void): Promise<void> => {
    const success = await exitTeam();
    if (success) {
      showTipsFn?.('已退出队伍');
      showExitTeam.value = false;
    }
  };

  /**
   * 触发领取奖励（供外部调用）
   */
  const triggerClaimAward = async (
    type: 'team' | 'captain',
    checkLoginFn?: () => Promise<boolean>,
    showTipsFn?: (msg: string) => void,
    key?: string,
    index?: number,
    getLangTxtFn?: (code: number, defaultMsg: string) => string,
    refreshBasicFn?: () => Promise<void>
  ): Promise<void> => {
    // 检查登录
    if (checkLoginFn) {
      const isLogin = await checkLoginFn();
      if (!isLogin) return;
    }

    const result = await claimAward(type, key, index);
    if (result.success) {
      showTipsFn?.('領取成功');
      // 刷新 basic 接口更新积分
      if (refreshBasicFn) {
        await refreshBasicFn();
      }
    } else {
      // 失败时也显示错误提示
      let errorMsg = result.message || '領取失敗';
      if (result.code && getLangTxtFn) {
        errorMsg = getLangTxtFn(result.code, '請稍後再試');
      }
      showTipsFn?.(errorMsg);
    }
  };

  // ========== 返回值 ==========
  return {
    // 状态
    state,
    showEventRule,
    showBreakTeam,
    showExitTeam,
    showAwardDetail,
    showBreakTeamTip,
    currentAwardDetail,

    // 计算属性
    isTeamFull,
    hasTeam,

    // 基础方法
    initTeamInfo,
    refreshTeamInfo,
    createTeam,
    breakTeam,
    exitTeam,
    inviteFriend,
    joinOtherTeam,
    claimAward,
    viewTeamDetails,
    getTeamMembers,
    viewAwardDetails,

    // 暴露给外部使用的触发方法（供任务侧等调用）
    triggerInvite,
    triggerCreateTeam,
    triggerBreakTeam,
    confirmBreakTeam,
    triggerExitTeam,
    confirmExitTeam,
    triggerClaimAward,
  };
}
